<template>
  <Modal @close="$emit('close')" size="md">
    <template #body>
      <div class="p-6">
        <h2 class="text-2xl font-bold text-center mb-2">Account Required to Publish</h2>
        <p class="text-gray-600 text-center mb-6">Sign in or create an account to complete your campaign setup</p>
        
        <!-- Tab Navigation -->
        <div class="flex mb-6 border-b border-gray-200">
          <button 
            @click="activeTab = 'login'"
            :class="[
              'flex-1 py-2 px-4 text-center font-medium transition-colors',
              activeTab === 'login' 
                ? 'text-pink-600 border-b-2 border-pink-600' 
                : 'text-gray-500 hover:text-gray-700'
            ]"
          >
            Sign In
          </button>
          <button 
            @click="activeTab = 'register'"
            :class="[
              'flex-1 py-2 px-4 text-center font-medium transition-colors',
              activeTab === 'register' 
                ? 'text-pink-600 border-b-2 border-pink-600' 
                : 'text-gray-500 hover:text-gray-700'
            ]"
          >
            Create Account
          </button>
        </div>
        
        <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {{ errorMessage }}
        </div>

        <!-- Login Tab -->
        <form v-if="activeTab === 'login'" @submit.prevent="handleLogin" class="space-y-4">
          <div>
            <label for="login-email" class="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input 
              id="login-email" 
              v-model="loginForm.email" 
              type="email" 
              autocomplete="email" 
              required 
              class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="Enter your email"
            >
          </div>

          <div>
            <label for="login-password" class="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input 
              id="login-password" 
              v-model="loginForm.password" 
              type="password" 
              autocomplete="current-password" 
              required 
              class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="Enter your password"
            >
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input 
                id="remember-me" 
                v-model="loginForm.rememberMe" 
                type="checkbox" 
                class="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
              >
              <label for="remember-me" class="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>

            <div class="text-sm">
              <button type="button" @click="activeTab = 'forgot'" class="font-medium text-pink-600 hover:text-pink-500">
                Forgot password?
              </button>
            </div>
          </div>

          <div>
            <button 
              type="submit" 
              :disabled="isLoading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="!isLoading">Sign in</span>
              <span v-else class="flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            </button>
          </div>
        </form>

        <!-- Register Tab -->
        <form v-if="activeTab === 'register'" @submit.prevent="handleRegister" class="space-y-4">
          <!-- Hidden honeypot field -->
          <div class="hidden">
            <label for="fax" class="block text-sm font-medium text-gray-700">
              Fax
            </label>
            <input 
              id="fax" 
              v-model="registerForm.fax" 
              type="text" 
              autocomplete="fax" 
              class="appearance-none block w-full px-3 py-2 border border-gray-300 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
          </div>

          <!-- Cloudflare Turnstile -->
          <div ref="turnstileContainer" class="mt-2"></div>

          <div>
            <label for="register-name" class="block text-sm font-medium text-gray-700 mb-1">
              Your Full Name
            </label>
            <input 
              id="register-name" 
              v-model="registerForm.name" 
              type="text" 
              autocomplete="name" 
              required 
              class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="Enter your full name"
            >
          </div>

          <div>
            <label for="register-email" class="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input 
              id="register-email" 
              v-model="registerForm.email" 
              type="email" 
              autocomplete="email" 
              required 
              class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="Enter your email"
            >
          </div>

          <div>
            <label for="register-address" class="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input 
              id="register-address" 
              v-model="registerForm.address" 
              type="text" 
              autocomplete="address" 
              required 
              class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="Enter your address"
            >
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label for="register-city" class="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input 
                id="register-city" 
                v-model="registerForm.city" 
                type="text" 
                autocomplete="city" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="City"
              >
            </div>
            
            <div>
              <label for="register-state" class="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <select 
                id="register-state" 
                v-model="registerForm.state" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              >
                <option value="">-- Select One --</option>
                <option v-for="state in states" :key="state.value" :value="state.value">
                  {{ state.label }}
                </option>
              </select>
            </div>
            
            <div>
              <label for="register-phone" class="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input 
                id="register-phone" 
                v-model="registerForm.phone" 
                type="text" 
                autocomplete="phone" 
                required 
                maxlength="16"
                @keydown="enforcePhoneFormat"
                @keyup="formatToPhone"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="(123) 456-7890"
              >
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label for="register-password" class="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input 
                id="register-password" 
                v-model="registerForm.password" 
                type="password" 
                autocomplete="new-password" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="Create a password"
              >
            </div>

            <div>
              <label for="register-password-confirm" class="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input 
                id="register-password-confirm" 
                v-model="registerForm.passwordConfirmation" 
                type="password" 
                autocomplete="new-password" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="Confirm your password"
              >
            </div>
          </div>

          <div>
            <button 
              type="submit" 
              :disabled="isLoading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="!isLoading">Create Account</span>
              <span v-else class="flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Account...
              </span>
            </button>
          </div>
        </form>

        <!-- Forgot Password Tab -->
        <form v-if="activeTab === 'forgot'" @submit.prevent="handleForgotPassword" class="space-y-4">
          <div v-if="forgotPasswordSuccess" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            <p class="font-medium">Check your email!</p>
            <p class="text-sm mt-1">We've sent a password reset link to {{ forgotForm.email }}</p>
          </div>
          
          <div v-else>
            <p class="text-gray-600 mb-4">Enter your email address and we'll send you a link to reset your password.</p>
            
            <div>
              <label for="forgot-email" class="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input 
                id="forgot-email" 
                v-model="forgotForm.email" 
                type="email" 
                autocomplete="email" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="Enter your email"
              >
            </div>

            <div>
              <button 
                type="submit" 
                :disabled="isLoading"
                class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!isLoading">Send Reset Link</span>
                <span v-else class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              </button>
            </div>
            
            <div class="text-center mt-4">
              <button type="button" @click="activeTab = 'login'" class="text-sm text-pink-600 hover:text-pink-500">
                Back to Sign In
              </button>
            </div>
          </div>
        </form>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '../ui/Modal.vue';
import axios from 'axios';

export default {
  name: 'AuthModal',
  
  components: {
    Modal
  },
  
  props: {
    initialEmail: {
      type: String,
      default: ''
    },
    initialTab: {
      type: String,
      default: 'login',
      validator: (value) => ['login', 'register', 'forgot'].includes(value)
    }
  },
  
  data() {
    return {
      activeTab: this.initialTab,
      isLoading: false,
      errorMessage: '',
      loginForm: {
        email: '',
        password: '',
        rememberMe: false
      },
      registerForm: {
        name: '',
        email: '',
        address: '',
        city: '',
        state: '',
        phone: '',
        password: '',
        passwordConfirmation: '',
        fax: '', // honeypot field
        formTime: 0 // for spam protection
      },
      turnstileToken: null,
      turnstileWidgetId: null,
      forgotForm: {
        email: ''
      },
      forgotPasswordSuccess: false,
      formStartTime: 0,
      states: [
        { value: 'AL', label: 'Alabama' },
        { value: 'AK', label: 'Alaska' },
        { value: 'AZ', label: 'Arizona' },
        { value: 'AR', label: 'Arkansas' },
        { value: 'CA', label: 'California' },
        { value: 'CO', label: 'Colorado' },
        { value: 'CT', label: 'Connecticut' },
        { value: 'DE', label: 'Delaware' },
        { value: 'DC', label: 'District Of Columbia' },
        { value: 'FL', label: 'Florida' },
        { value: 'GA', label: 'Georgia' },
        { value: 'HI', label: 'Hawaii' },
        { value: 'ID', label: 'Idaho' },
        { value: 'IL', label: 'Illinois' },
        { value: 'IN', label: 'Indiana' },
        { value: 'IA', label: 'Iowa' },
        { value: 'KS', label: 'Kansas' },
        { value: 'KY', label: 'Kentucky' },
        { value: 'LA', label: 'Louisiana' },
        { value: 'ME', label: 'Maine' },
        { value: 'MD', label: 'Maryland' },
        { value: 'MA', label: 'Massachusetts' },
        { value: 'MI', label: 'Michigan' },
        { value: 'MN', label: 'Minnesota' },
        { value: 'MS', label: 'Mississippi' },
        { value: 'MO', label: 'Missouri' },
        { value: 'MT', label: 'Montana' },
        { value: 'NE', label: 'Nebraska' },
        { value: 'NV', label: 'Nevada' },
        { value: 'NH', label: 'New Hampshire' },
        { value: 'NJ', label: 'New Jersey' },
        { value: 'NM', label: 'New Mexico' },
        { value: 'NY', label: 'New York' },
        { value: 'NC', label: 'North Carolina' },
        { value: 'ND', label: 'North Dakota' },
        { value: 'OH', label: 'Ohio' },
        { value: 'OK', label: 'Oklahoma' },
        { value: 'OR', label: 'Oregon' },
        { value: 'PA', label: 'Pennsylvania' },
        { value: 'RI', label: 'Rhode Island' },
        { value: 'SC', label: 'South Carolina' },
        { value: 'SD', label: 'South Dakota' },
        { value: 'TN', label: 'Tennessee' },
        { value: 'TX', label: 'Texas' },
        { value: 'UT', label: 'Utah' },
        { value: 'VT', label: 'Vermont' },
        { value: 'VA', label: 'Virginia' },
        { value: 'WA', label: 'Washington' },
        { value: 'WV', label: 'West Virginia' },
        { value: 'WI', label: 'Wisconsin' },
        { value: 'WY', label: 'Wyoming' }
      ]
    };
  },
  
  methods: {
    // Phone number formatting methods
    isNumericInput(event) {
      const key = event.keyCode;
      return ((key >= 48 && key <= 57) || // Allow number line
          (key >= 96 && key <= 105) // Allow number pad
      );
    },
    
    isModifierKey(event) {
      const key = event.keyCode;
      return (event.shiftKey === true || key === 35 || key === 36) || // Allow Shift, Home, End
          (key === 8 || key === 9 || key === 13 || key === 46) || // Allow Backspace, Tab, Enter, Delete
          (key > 36 && key < 41) || // Allow left, up, right, down
          (
              // Allow Ctrl/Command + A,C,V,X,Z
              (event.ctrlKey === true || event.metaKey === true) &&
              (key === 65 || key === 67 || key === 86 || key === 88 || key === 90)
          );
    },
    
    enforcePhoneFormat(event) {
      // Input must be of a valid number format or a modifier key
      if(!this.isNumericInput(event) && !this.isModifierKey(event)) {
        event.preventDefault();
      }
    },
    
    formatToPhone(event) {
      if(this.isModifierKey(event)) { return; }

      const input = event.target.value.replace(/\D/g,'').substring(0,10); // First ten digits of input only
      const areaCode = input.substring(0,3);
      const middle = input.substring(3,6);
      const last = input.substring(6,10);

      if(input.length > 6) {
        event.target.value = `(${areaCode}) ${middle} - ${last}`;
      } else if(input.length > 3) {
        event.target.value = `(${areaCode}) ${middle}`;
      } else if(input.length > 0) {
        event.target.value = `(${areaCode}`;
      }
      
      // Update the model value
      this.registerForm.phone = event.target.value;
    },
    
    loadTurnstile() {
      if (typeof window.turnstile !== 'undefined') {
        this.renderTurnstile();
        return;
      }
      if (!document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoadModal';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        window.onTurnstileLoadModal = () => {
          this.renderTurnstile();
        };
      } else {
        // Script already exists but may not have loaded yet
        const checkInterval = setInterval(() => {
          if (typeof window.turnstile !== 'undefined') {
            clearInterval(checkInterval);
            this.renderTurnstile();
          }
        }, 100);
        setTimeout(() => clearInterval(checkInterval), 5000);
      }
    },

    renderTurnstile() {
      if (!this.$refs.turnstileContainer || typeof window.turnstile === 'undefined') return;
      if (this.turnstileWidgetId !== null) return; // already rendered
      const siteKey = document.querySelector('meta[name="turnstile-site-key"]')?.getAttribute('content');
      if (!siteKey) return;
      this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileContainer, {
        sitekey: siteKey,
        appearance: 'interaction-only',
        theme: 'light',
        callback: (token) => { this.turnstileToken = token; },
        'error-callback': () => { this.turnstileToken = null; }
      });
    },

    async handleLogin() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || 
                     document.querySelector('input[name="_token"]')?.value;
        
        const response = await axios.post('/login', {
          email: this.loginForm.email,
          password: this.loginForm.password,
          remember: this.loginForm.rememberMe,
          _token: token
        });
        
        if (response.data.success || response.status === 200) {
          // Show success message
          this.errorMessage = '';
          this.$emit('auth-success', response.data.user || {});
          
          // Show success notice in the modal
          const successMessage = document.createElement('div');
          successMessage.className = 'bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4';
          successMessage.innerHTML = `
            <p class="font-bold">Success! You are now logged in.</p>
            <p>You can close this modal and continue with your campaign setup.</p>
          `;
          
          // Replace form with success message
          const formElement = this.$el.querySelector('form');
          if (formElement) {
            formElement.style.display = 'none';
            formElement.parentNode.insertBefore(successMessage, formElement);
          }
          
          // Close modal after 2 seconds
          setTimeout(() => {
            this.$emit('close');
          }, 2000);
        } else {
          this.errorMessage = response.data.message || 'Login failed. Please try again.';
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          const errors = error.response.data.errors;
          if (errors) {
            this.errorMessage = Object.values(errors).flat().join(' ');
          } else {
            this.errorMessage = error.response.data.message || 'Invalid email or password.';
          }
        } else if (error.response && error.response.status === 401) {
          this.errorMessage = 'Invalid email or password.';
        } else {
          this.errorMessage = 'An error occurred. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    async handleRegister() {
      this.isLoading = true;
      this.errorMessage = '';
      
      if (this.registerForm.password !== this.registerForm.passwordConfirmation) {
        this.errorMessage = 'Passwords do not match.';
        this.isLoading = false;
        return;
      }
      
      // Calculate form submission time
      const elapsedTime = new Date().getTime() - this.formStartTime;
      this.registerForm.formTime = elapsedTime;
      
      try {
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || 
                     document.querySelector('input[name="_token"]')?.value;
        
        const response = await axios.post('/user-register', {
          name: this.registerForm.name,
          email: this.registerForm.email,
          address: this.registerForm.address,
          city: this.registerForm.city,
          state: this.registerForm.state,
          phone: this.registerForm.phone,
          password: this.registerForm.password,
          password_confirmation: this.registerForm.passwordConfirmation,
          fax: this.registerForm.fax,
          formTime: this.registerForm.formTime,
          'cf-turnstile-response': this.turnstileToken,
          'turnstile_available': this.turnstileToken ? '1' : '0',
          _token: token
        });
        
        if (response.data.success || response.status === 200 || response.status === 201) {
          // Show success message
          this.errorMessage = '';
          this.$emit('auth-success', response.data.user || {});
          
          // Show success notice in the modal
          const successMessage = document.createElement('div');
          successMessage.className = 'bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4';
          successMessage.innerHTML = `
            <p class="font-bold">Success! Your account has been created.</p>
            <p>You can close this modal and continue with your campaign setup.</p>
          `;
          
          // Replace form with success message
          const formElement = this.$el.querySelector('form');
          if (formElement) {
            formElement.style.display = 'none';
            formElement.parentNode.insertBefore(successMessage, formElement);
          }
          
          // Close modal after 2 seconds
          setTimeout(() => {
            this.$emit('close');
          }, 2000);
        } else {
          this.errorMessage = response.data.message || 'Registration failed. Please try again.';
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          const errors = error.response.data.errors;
          if (errors) {
            this.errorMessage = Object.values(errors).flat().join(' ');
          } else {
            this.errorMessage = error.response.data.message || 'Registration failed.';
          }
        } else {
          this.errorMessage = 'An error occurred. Please try again.';
        }

        // Reset Turnstile for retry
        if (typeof window.turnstile !== 'undefined' && this.turnstileWidgetId !== null) {
          window.turnstile.reset(this.turnstileWidgetId);
          this.turnstileToken = null;
        }
      } finally {
        this.isLoading = false;
      }
    },

    async handleForgotPassword() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || 
                     document.querySelector('input[name="_token"]')?.value;
        
        const response = await axios.post('/password/email-ajax', {
          email: this.forgotForm.email,
          _token: token
        });
        
        if (response.data.success) {
          this.forgotPasswordSuccess = true;
          this.errorMessage = '';
        } else {
          this.errorMessage = response.data.message || 'Failed to send reset link. Please try again.';
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          const errors = error.response.data.errors;
          if (errors && errors.email) {
            this.errorMessage = errors.email[0];
          } else {
            this.errorMessage = error.response.data.message || 'Please check your email address.';
          }
        } else {
          this.errorMessage = 'An error occurred. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    }
  },
  
  mounted() {
    // Start form timer for spam protection
    this.formStartTime = new Date().getTime();
    this.loadTurnstile();

    // Pre-fill email if provided
    if (this.initialEmail) {
      if (this.activeTab === 'login') {
        this.loginForm.email = this.initialEmail;
      } else if (this.activeTab === 'register') {
        this.registerForm.email = this.initialEmail;
      } else if (this.activeTab === 'forgot') {
        this.forgotForm.email = this.initialEmail;
      }
    }
    
    // Focus on appropriate input when modal opens
    this.$nextTick(() => {
      if (this.activeTab === 'login') {
        // If email is pre-filled, focus on password
        const focusField = this.initialEmail ? 'login-password' : 'login-email';
        document.getElementById(focusField)?.focus();
      } else if (this.activeTab === 'register') {
        // Always focus on name for registration
        document.getElementById('register-name')?.focus();
      } else if (this.activeTab === 'forgot') {
        // Focus on email for forgot password
        document.getElementById('forgot-email')?.focus();
      }
    });
  },
  
  watch: {
    activeTab(newTab) {
      // Clear error message when switching tabs
      this.errorMessage = '';

      // Reset forgot password success message
      if (newTab !== 'forgot') {
        this.forgotPasswordSuccess = false;
      }

      // Re-render Turnstile when switching to register tab
      if (newTab === 'register') {
        this.$nextTick(() => {
          this.renderTurnstile();
        });
      }

      // Focus on first input of the active tab
      this.$nextTick(() => {
        let firstInput;
        if (newTab === 'login') {
          firstInput = document.getElementById('login-email');
        } else if (newTab === 'register') {
          firstInput = document.getElementById('register-name');
        } else if (newTab === 'forgot') {
          firstInput = document.getElementById('forgot-email');
        }
        firstInput?.focus();
      });
    }
  }
};
</script>